/**
 * Chat summary query operations - Get
 */

import { getSupabaseClient } from '../../supabase-client.js';
import type { ChatId, UserId } from '../../types.js';
import type { ChatSummary, QueryOptions, QueryResult } from '../types.js';
import { logger } from '../../../logger.js';
import { mapChatSummaryRow, type ChatSummaryRow } from './row-mapper.js';

/**
 * Get a chat summary by chat ID
 */
export async function getChatSummaryById(
  chatId: ChatId
): Promise<ChatSummary | null> {
  try {
    const client = getSupabaseClient();

    const { data, error } = await client
      .from('meridian_chat_summaries')
      .select('*')
      .eq('chat_id', chatId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows found
        return null;
      }
      throw error;
    }

    return mapChatSummaryRow(data as ChatSummaryRow);
  } catch (error) {
    logger.error('error getting chat summary by id', error, { op: 'getChatSummaryById' });
    throw error;
  }
}

/**
 * Get all chat summaries for a user
 */
export async function getChatSummariesByUser(
  userId: UserId,
  options?: QueryOptions
): Promise<QueryResult<ChatSummary>> {
  try {
    const client = getSupabaseClient();
    const limit = options?.limit || 50;
    const offset = options?.offset || 0;
    const ascending = options?.ascending ?? false;

    const { data, error, count } = await client
      .from('meridian_chat_summaries')
      .select('*', { count: 'exact' })
      .eq('user_id', userId)
      .order('updated_at', { ascending })
      .range(offset, offset + limit - 1);

    if (error) {
      throw error;
    }

    const items = (data || []).map((row) => mapChatSummaryRow(row as ChatSummaryRow));

    const hasMore = count ? offset + limit < count : false;

    return {
      items,
      hasMore,
      offset,
    };
  } catch (error) {
    logger.error('error getting chat summaries by user', error, { op: 'getChatSummariesByUser' });
    throw error;
  }
}

